(() => {
  'use strict';

  const FREE_SHIPPING_THRESHOLD = 5900;
  const VITALSOP_PRODUCT_ID = 9781012365634;
  const NEW_30_VARIANT = '56022016688450';
  const NEW_90_VARIANT = '56022016721218';
  const LEGACY_90_VARIANT = '55980623429954';
  const OFFER_VARIANTS = new Set([
    '55979769037122', '55980623397186', LEGACY_90_VARIANT,
    '56022016655682', NEW_30_VARIANT, NEW_90_VARIANT
  ]);
  const GIFT_VARIANTS = {
    countertop: 54953957130562,
    tray: 55972557553986,
    pouch: 55972557521218
  };
  const GIFT_CART_VARIANTS = new Set(Object.values(GIFT_VARIANTS).map(String));
  const CART_UPGRADES = {
    subscribe: {
      variantId: '56022016688450',
      planId: '692749304130',
      supplyDays: '30',
      badge: 'SUBSCRIBE & SAVE',
      title: 'Upgrade to Subscribe & Save',
      benefits: [
        'Save on every bottle',
        'Skip, pause, or cancel anytime'
      ],
      button: 'Upgrade & Save'
    },
    bestValue: {
      variantId: NEW_90_VARIANT,
      planId: '692749402434',
      supplyDays: '90',
      badge: 'MOST POPULAR',
      title: 'Buy Two, Get One Free',
      benefits: [
        '3rd bottle free every delivery',
        'Unlock future loyalty gifts',
        'Backed by a 90-Day Guarantee'
      ],
      button: 'Yes ΓÇö Get 3 Bottles for $79.95'
    }
  };
  const MANAGED_CART_VARIANTS = new Set([
    ...OFFER_VARIANTS,
    ...Object.values(GIFT_VARIANTS).map(String)
  ]);
  let clearMutationTimer;
  let giftCleanupTimer;
  let giftCleanupRunning = false;
  let checkoutResuming = false;
  let enhanceFrame;
  let bundleSubmitting = false;
  const UPGRADE_TRACKING_STARTED_AT = '2026-08-24T14:30:00-07:00';

  function trackingKey(stage, cartToken, targetVariant) {
    return `vitalsop:cart-upgrade:${stage}:${cartToken || 'unknown'}:${targetVariant}`;
  }

  function trackUpgradeEvent(name, payload = {}, dedupeStage = '') {
    const cart = currentCart();
    const targetVariant = String(payload.target_variant_id || CART_UPGRADES.bestValue.variantId);
    const key = dedupeStage ? trackingKey(dedupeStage, cart?.token, targetVariant) : '';
    if (key) {
      try {
        if (sessionStorage.getItem(key)) return false;
        sessionStorage.setItem(key, '1');
      } catch (_) {}
    }
    const event = {
      tracking_started_at: UPGRADE_TRACKING_STARTED_AT,
      cart_token: cart?.token || '',
      source: 'zipify_cart_drawer_custom_upgrade',
      ...payload
    };
    if (window.__vitalsop_tracking_test_mode) {
      window.__vitalsop_tracking_test_events = window.__vitalsop_tracking_test_events || [];
      window.__vitalsop_tracking_test_events.push({ name, event });
      return true;
    }
    window._learnq = window._learnq || [];
    window._learnq.push(['track', name, event]);
    const normalizedName = name.toLowerCase().replace(/\s+/g, '_');
    if (typeof window.gtag === 'function') {
      window.gtag('event', normalizedName, event);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: normalizedName, ...event });
    }
    try {
      const published = window.Shopify?.analytics?.publish?.(
        normalizedName,
        event
      );
      if (published?.catch) published.catch(() => {});
    } catch (_) {}
    return true;
  }

  function markCartMutating() {
    clearTimeout(clearMutationTimer);
    document.documentElement.classList.add('vitalsop-cart-mutating');
  }

  function clearCartMutatingSoon() {
    if (bundleSubmitting) return;
    clearTimeout(clearMutationTimer);
    clearMutationTimer = setTimeout(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        enhanceCart();
        document.documentElement.classList.remove('vitalsop-cart-mutating');
      }));
    }, 80);
  }

  function isCartMutationUrl(value) {
    const url = typeof value === 'string' ? value : (value && value.url) || '';
    return /\/cart\/(add|change|update|clear)(?:\.js)?(?:[?#]|$)/i.test(url);
  }

  async function reconcileGiftLines() {
    if (giftCleanupRunning) {
      while (giftCleanupRunning) await new Promise(resolve => setTimeout(resolve, 25));
      return fetchCartJson('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
    }

    giftCleanupRunning = true;
    try {
      let cart = await fetchCartJson('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      const subscriptions = (cart.items || []).filter(item => (
        Number(item.product_id) === VITALSOP_PRODUCT_ID
        && Boolean(item.selling_plan_allocation)
        && !GIFT_CART_VARIANTS.has(String(item.variant_id))
      ));
      const legacyBestValueQuantity = subscriptions.reduce((total, item) => (
        String(item.variant_id) === LEGACY_90_VARIANT
          ? total + Math.max(0, Number(item.quantity) || 0)
          : total
      ), 0);
      const countertopQuantity = subscriptions.reduce((total, item) => (
        ![NEW_30_VARIANT, NEW_90_VARIANT].includes(String(item.variant_id))
          ? total + Math.max(0, Number(item.quantity) || 0)
          : total
      ), 0);
      const desiredGiftQuantities = new Map([
        [String(GIFT_VARIANTS.tray), legacyBestValueQuantity],
        [String(GIFT_VARIANTS.pouch), legacyBestValueQuantity],
        [String(GIFT_VARIANTS.countertop), countertopQuantity]
      ]);
      const foundGifts = new Set();
      const updates = {};

      (cart.items || []).forEach(item => {
        const variantId = String(item.variant_id);
        if (!GIFT_CART_VARIANTS.has(variantId)) return;
        const desiredQuantity = desiredGiftQuantities.get(variantId) || 0;
        if (!desiredQuantity || foundGifts.has(variantId)) {
          updates[item.key] = 0;
          return;
        }
        foundGifts.add(variantId);
        if (Number(item.quantity) !== desiredQuantity) updates[item.key] = desiredQuantity;
      });

      const missingItems = Array.from(desiredGiftQuantities.entries())
        .filter(([variantId, quantity]) => quantity > 0 && !foundGifts.has(variantId))
        .map(([id, quantity]) => ({
          id,
          quantity,
          properties: { _vitalsop_welcome_gift_for: 'qualifying subscription' }
        }));

      if (!missingItems.length && !Object.keys(updates).length) return cart;
      markCartMutating();

      // Add first so a qualifying cart never loses its gifts between requests.
      if (missingItems.length) {
        cart = await fetchCartJson('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: missingItems })
        });
      }
      if (Object.keys(updates).length) {
        cart = await fetchCartJson('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates })
        });
      }
      if (!cart?.token || !Array.isArray(cart.items)) {
        cart = await fetchCartJson('/cart.js', { headers: { Accept: 'application/json' } });
      }

      const ocu = window.Zipify?.Cart?.instance;
      if (ocu?.updateCart) await Promise.resolve(ocu.updateCart()).catch(() => {});
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart }
      }));
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart }
      }));
      scheduleEnhance();
      return cart;
    } catch (error) {
      console.warn('[VitalSop gifts] Unable to reconcile welcome gifts', error);
      return null;
    } finally {
      giftCleanupRunning = false;
      clearCartMutatingSoon();
    }
  }

  function scheduleGiftCleanup() {
    clearTimeout(giftCleanupTimer);
    giftCleanupTimer = setTimeout(async () => {
      if (bundleSubmitting) {
        scheduleGiftCleanup();
        return;
      }
      await reconcileGiftLines();
    }, 450);
  }

  async function continueToCheckout(trigger) {
    while (bundleSubmitting) await new Promise(resolve => setTimeout(resolve, 25));
    const cart = await reconcileGiftLines();
    const subscriptions = (cart?.items || []).filter(item => (
      Number(item.product_id) === VITALSOP_PRODUCT_ID
      && Boolean(item.selling_plan_allocation)
      && !GIFT_CART_VARIANTS.has(String(item.variant_id))
    ));
    const legacyBestValueQuantity = subscriptions.reduce((total, item) => (
      String(item.variant_id) === LEGACY_90_VARIANT
        ? total + Math.max(0, Number(item.quantity) || 0)
        : total
    ), 0);
    const countertopQuantity = subscriptions.reduce((total, item) => (
      ![NEW_30_VARIANT, NEW_90_VARIANT].includes(String(item.variant_id))
        ? total + Math.max(0, Number(item.quantity) || 0)
        : total
    ), 0);
    const requiredGiftQuantities = new Map([
      [String(GIFT_VARIANTS.tray), legacyBestValueQuantity],
      [String(GIFT_VARIANTS.pouch), legacyBestValueQuantity],
      [String(GIFT_VARIANTS.countertop), countertopQuantity]
    ]);
    const actualGiftQuantities = new Map();
    (cart?.items || []).forEach(item => {
      const variantId = String(item.variant_id);
      if (!GIFT_CART_VARIANTS.has(variantId)) return;
      actualGiftQuantities.set(
        variantId,
        (actualGiftQuantities.get(variantId) || 0) + Math.max(0, Number(item.quantity) || 0)
      );
    });
    const giftsConfirmed = Array.from(requiredGiftQuantities.entries()).every(
      ([variantId, quantity]) => (actualGiftQuantities.get(variantId) || 0) === quantity
    );

    if (!cart || !giftsConfirmed) {
      window.alert('We could not confirm your free gifts. Please try Checkout again.');
      return;
    }

    checkoutResuming = true;
    try {
      if (trigger?.click) trigger.click();
      else window.location.assign('/checkout');
    } finally {
      setTimeout(() => { checkoutResuming = false; }, 0);
    }
  }

  document.addEventListener('click', event => {
    if (checkoutResuming) return;
    const trigger = event.target.closest('.ocu-checkout-button, [name="checkout"], a[href*="/checkout"]');
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    continueToCheckout(trigger);
  }, true);

  document.addEventListener('submit', event => {
    if (checkoutResuming) return;
    const form = event.target;
    if (!(form?.matches?.('#ocu-cart-form, form[action*="/checkout"]'))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    continueToCheckout(event.submitter);
  }, true);

  function cartMutationSettled() {
    clearCartMutatingSoon();
    scheduleGiftCleanup();
  }

  const nativeFetch = window.fetch;
  if (nativeFetch) {
    window.fetch = function (...args) {
      if (!isCartMutationUrl(args[0])) return nativeFetch.apply(this, args);
      markCartMutating();
      return nativeFetch.apply(this, args).finally(cartMutationSettled);
    };
  }

  const nativeXhrOpen = XMLHttpRequest.prototype.open;
  const nativeXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__vitalsopCartMutation = isCartMutationUrl(url);
    return nativeXhrOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__vitalsopCartMutation) {
      markCartMutating();
      this.addEventListener('loadend', cartMutationSettled, { once: true });
    }
    return nativeXhrSend.apply(this, args);
  };

  function selectedManagedOffer() {
    const radio = document.querySelector('.offer-card__radio-input:checked');
    if (!radio || !OFFER_VARIANTS.has(String(radio.dataset.variantId))) return null;
    if (radio.dataset.onetime !== 'true' && !radio.dataset.planId) return null;
    return radio;
  }

  function giftItemsForOffer(radio) {
    if (radio.dataset.onetime === 'true') return [];
    if ([NEW_30_VARIANT, NEW_90_VARIANT].includes(String(radio.dataset.variantId))) return [];
    const giftFor = radio.dataset.supplyDays === '90' ? '90-day subscription' : '30-day subscription';
    const ids = radio.dataset.supplyDays === '90'
      ? [GIFT_VARIANTS.tray, GIFT_VARIANTS.pouch, GIFT_VARIANTS.countertop]
      : [GIFT_VARIANTS.countertop];
    return ids.map(id => ({
      id,
      quantity: 1,
      properties: { _vitalsop_welcome_gift_for: giftFor }
    }));
  }

  async function fetchCartJson(url, options) {
    // Use the browser's original fetch so OCU does not render intermediate
    // cart states while an offer is being swapped.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const response = await nativeFetch(url, options);
      if (response.ok) return response.json();
      const detail = await response.text().catch(() => '');
      if (response.status !== 429 || attempt === 2) {
        throw new Error(`Cart request failed (${response.status}) ${detail}`.trim());
      }
      await new Promise(resolve => setTimeout(resolve, 600 * (2 ** attempt)));
    }
    return null;
  }

  async function replaceOfferBundle(radio, button) {
    if (bundleSubmitting) return;
    bundleSubmitting = true;
    markCartMutating();
    if (button) {
      button.dataset.originalText = button.textContent;
      button.textContent = 'UpgradingΓÇª';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    let tracking = null;
    try {
      // OCU's in-memory cart can lag behind the rendered drawer. Always snapshot
      // Shopify directly before replacing lines so cleanup uses the real keys.
      const current = await fetchCartJson('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      const oldMain = (current.items || []).find(item => (
        Number(item.product_id) === VITALSOP_PRODUCT_ID
        && !GIFT_CART_VARIANTS.has(String(item.variant_id))
      ));
      if (radio.dataset.cartUpgrade === 'true') {
        tracking = {
          upgrade_id: crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          clicked_at: new Date().toISOString(),
          from_variant_id: String(oldMain?.variant_id || ''),
          from_selling_plan: oldMain?.selling_plan_allocation?.selling_plan?.name || 'one-time',
          from_line_revenue_cents: Number(oldMain?.final_line_price || 0),
          target_variant_id: String(radio.dataset.variantId),
          target_selling_plan_id: String(radio.dataset.planId || ''),
          target_offer: radio.dataset.supplyDays === '90' ? '90-day subscription' : '30-day subscription'
        };
        trackUpgradeEvent('VitalSop Cart Upgrade Clicked', tracking, 'clicked');
      }
      const stableLineIdentity = item => JSON.stringify({
        variantId: String(item.variant_id),
        sellingPlanId: String(item.selling_plan_allocation?.selling_plan?.id || ''),
        properties: Object.entries(item.properties || {})
          .filter(([, value]) => value != null && value !== '')
          .sort(([left], [right]) => left.localeCompare(right))
      });
      const oldManagedLines = [];
      (current.items || []).forEach(item => {
        if (!MANAGED_CART_VARIANTS.has(String(item.variant_id))) return;
        oldManagedLines.push({
          key: item.key,
          identity: stableLineIdentity(item),
          remaining: Math.max(0, Number(item.quantity) || 0)
        });
      });

      const parentItem = {
        id: Number(radio.dataset.variantId),
        quantity: 1
      };
      if (radio.dataset.planId) parentItem.selling_plan = Number(radio.dataset.planId);
      if (tracking) {
        parentItem.properties = {
          _vitalsop_cart_upgrade: 'true',
          _vitalsop_upgrade_id: tracking.upgrade_id,
          _vitalsop_upgrade_from_variant: tracking.from_variant_id,
          _vitalsop_upgrade_from_plan: tracking.from_selling_plan,
          _vitalsop_upgrade_from_revenue_cents: String(tracking.from_line_revenue_cents),
          _vitalsop_upgrade_to: tracking.target_offer,
          _vitalsop_upgrade_at: tracking.clicked_at,
          _vitalsop_tracking_started_at: UPGRADE_TRACKING_STARTED_AT
        };
      }
      const items = [parentItem, ...giftItemsForOffer(radio)];

      await fetchCartJson('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });

      // Remove the previous offer only after the replacement exists. The cart
      // therefore never reaches zero items during an upgrade. Shopify can merge
      // replacement products or gifts into an existing line key, so subtract the
      // old quantity from that key instead of zeroing the entire merged line.
      let cart = await fetchCartJson('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      const oldLineUpdates = {};
      (cart.items || []).forEach(item => {
        if (!MANAGED_CART_VARIANTS.has(String(item.variant_id))) return;
        const identity = stableLineIdentity(item);
        const oldLine = oldManagedLines.find(line => line.remaining > 0 && line.key === item.key)
          || oldManagedLines.find(line => line.remaining > 0 && line.identity === identity);
        if (!oldLine) return;
        const currentQuantity = Math.max(0, Number(item.quantity) || 0);
        const quantityToRemove = Math.min(currentQuantity, oldLine.remaining);
        oldLineUpdates[item.key] = currentQuantity - quantityToRemove;
        oldLine.remaining -= quantityToRemove;
      });
      if (Object.keys(oldLineUpdates).length) {
        cart = await fetchCartJson('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: oldLineUpdates })
        });
      }

      if (!cart?.token || !Array.isArray(cart.items)) {
        cart = await fetchCartJson('/cart.js', { headers: { Accept: 'application/json' } });
      }
      cart = await reconcileGiftLines() || cart;
      const upgradedLine = (cart.items || []).find(item => (
        String(item.variant_id) === String(radio.dataset.variantId)
        && (
          !radio.dataset.planId
          || String(item.selling_plan_allocation?.selling_plan?.id || '') === String(radio.dataset.planId)
        )
      ));
      if (!upgradedLine) throw new Error('The upgraded offer was not confirmed in the Shopify cart.');

      if (tracking) {
        const upgradedRevenueCents = Number(upgradedLine.final_line_price || 0);
        const incrementalRevenueCents = Math.max(
          0,
          upgradedRevenueCents - tracking.from_line_revenue_cents
        );
        trackUpgradeEvent('VitalSop Cart Upgrade Completed', {
          ...tracking,
          completed_at: new Date().toISOString(),
          upgraded_line_revenue_cents: upgradedRevenueCents,
          incremental_revenue_cents: incrementalRevenueCents,
          $value: incrementalRevenueCents / 100,
          value: incrementalRevenueCents / 100,
          currency: cart.currency || 'USD'
        }, 'completed');
      }

      const ocu = window.Zipify?.Cart?.instance;
      try {
        if (ocu?.updateCart) {
          await Promise.resolve(ocu.updateCart());
          const refreshedCart = currentCart();
          if ((refreshedCart?.items || []).some(item => (
            String(item.variant_id) === String(radio.dataset.variantId)
          ))) cart = refreshedCart;
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        if (ocu?.openCart) ocu.openCart({ preventUpdate: true });
        else if (ocu?.openUpdatedCart) ocu.openUpdatedCart();
      } catch (refreshError) {
        console.warn('[VitalSop bundle] Bundle added, but the cart drawer refresh failed', refreshError);
      }
    } catch (error) {
      console.error('[VitalSop bundle] Unable to add offer bundle', error);
      if (tracking) {
        trackUpgradeEvent('VitalSop Cart Upgrade Failed', {
          ...tracking,
          failed_at: new Date().toISOString(),
          error: String(error?.message || error).slice(0, 250)
        });
      }
      window.alert('We could not add your VitalSop bundle. Please try again.');
    } finally {
      bundleSubmitting = false;
      if (button) {
        button.textContent = button.dataset.originalText || 'Upgrade & Save';
        delete button.dataset.originalText;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
      clearCartMutatingSoon();
    }
  }

  function interceptManagedAdd(event, button, form) {
    const radio = selectedManagedOffer();
    if (!radio) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    replaceOfferBundle(radio, button || form?.querySelector('[type="submit"]'));
    return true;
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('.product-form__submit, [name="add"], #sticky-atc-btn');
    if (!button) return;
    const form = button.closest('form') || document.querySelector('form[action*="/cart/add"]');
    interceptManagedAdd(event, button, form);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    interceptManagedAdd(event, form.querySelector('[type="submit"]'), form);
  }, true);

  document.addEventListener('click', (event) => {
    if (event.target.closest('.product-form__submit, [name="add"], #sticky-atc-btn')) {
      markCartMutating();
      clearTimeout(clearMutationTimer);
      clearMutationTimer = setTimeout(clearCartMutatingSoon, 4000);
    }
  }, true);

  function currentCart() {
    return window.Zipify?.Cart?.instance?.cart || window.Zipify?.Cart?.cart || null;
  }

  function money(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currentCart()?.currency || 'USD'
    }).format(cents / 100);
  }

  function bottleCount(item) {
    const source = `${item.variant_title || ''} ${item.title || ''}`;
    const match = source.match(/(\d+)\s*bottle/i);
    return match ? Number(match[1]) : Math.max(1, Number(item.quantity) || 1);
  }

  function selectedOfferText(item) {
    if (!item.selling_plan_allocation) return 'One-Time Purchase';
    if (String(item.variant_id) === CART_UPGRADES.bestValue.variantId) {
      return 'Buy 2 Get 1 Free';
    }
    return '30-Day Subscription';
  }

  function updateCadence(cart) {
    const rows = [...document.querySelectorAll('.ocu-cart-line-item-wrapper')];
    rows.forEach((row) => {
      const rowKey = String(row.dataset.key || '');
      const item = cart.items?.find(entry => String(entry.key || '') === rowKey);
      if (!item || Number(item.product_id) !== VITALSOP_PRODUCT_ID) return;
      const variant = row.querySelector('.ocu-cart-line-item-product-variant');
      const offerText = selectedOfferText(item);
      if (variant && variant.textContent.trim() !== offerText) variant.textContent = offerText;

      const isBestValue = String(item.variant_id) === CART_UPGRADES.bestValue.variantId
        && Boolean(item.selling_plan_allocation);
      let savings = row.querySelector('.vitalsop-cart-savings');
      if (!isBestValue) {
        if (savings) savings.remove();
        return;
      }
      if (!savings && variant) {
        savings = document.createElement('div');
        savings.className = 'vitalsop-cart-savings';
        savings.innerHTML = `
          <strong>You save $60</strong>
          <span>Your FREE 3rd bottle is already included</span>
        `;
        variant.insertAdjacentElement('afterend', savings);
      }
    });
  }

  function updateShipping(cart) {
    const banner = document.querySelector('.ocu-cart-banner');
    const message = banner?.querySelector('.ocu-cart-banner--message-text');
    if (!banner || !message) return;

    const subtotal = Number(cart.total_price ?? cart.items_subtotal_price ?? 0);
    const subscriptionQualifies = Boolean(
      cart.items?.some(item => item.selling_plan_allocation)
    );
    const remaining = subscriptionQualifies
      ? 0
      : Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
    const progress = subscriptionQualifies
      ? 100
      : Math.min(100, Math.max(0, subtotal / FREE_SHIPPING_THRESHOLD * 100));
    const text = remaining > 0
      ? `You're ${money(remaining)} away from FREE SHIPPING`
      : '≡ƒÄë Congrats! YouΓÇÖve unlocked FREE SHIPPING!';

    if (message.textContent !== text) message.textContent = text;
    let bar = banner.querySelector('.vitalsop-shipping-progress');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'vitalsop-shipping-progress';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<span></span>';
      banner.appendChild(bar);
    }
    const fill = bar.firstElementChild;
    const width = `${progress.toFixed(2)}%`;
    if (fill.style.width !== width) fill.style.width = width;
  }

  function updateTotalsPresentation() {
    const label = document.querySelector(
      '.ocu-estimated-info > .ocu-editable-cart-text:first-child span'
    );
    if (label && label.textContent.trim() !== 'Subtotal') {
      label.textContent = 'Subtotal';
    }
  }

  function updateGiftPresentation(cart) {
    const rows = [...document.querySelectorAll('.ocu-cart-line-item-wrapper')];
    rows.forEach((row) => {
      const variantId = String(row.dataset.key || '').split(':')[0];
      let properties = {};
      try {
        properties = JSON.parse(row.dataset.properties || '{}');
      } catch (_) {}
      const rowText = row.textContent || '';
      const isWelcomeGift = properties._vitalsop_welcome_gift === 'true'
        || Boolean(properties._vitalsop_welcome_gift_for)
        || GIFT_CART_VARIANTS.has(variantId)
        || /Counter\s*top reminder|Ritual tray|Travel Pouch/i.test(rowText);
      row.classList.toggle('vitalsop-ocu-hidden-gift', isWelcomeGift);
      if (isWelcomeGift) row.setAttribute('aria-hidden', 'true');
      else row.removeAttribute('aria-hidden');
    });

    const visibleQuantity = (cart.items || []).reduce((total, item) => {
      return GIFT_CART_VARIANTS.has(String(item.variant_id))
        ? total
        : total + Math.max(1, Number(item.quantity) || 1);
    }, 0);
    const visibleText = String(visibleQuantity);
    const count = document.querySelector('.ocu-cart-header--items-count span');
    if (count && count.textContent !== visibleText) count.textContent = visibleText;
    const accessibleCount = document.querySelector('[data-testid="cart-count"]');
    const accessibleText = `${visibleQuantity} ${visibleQuantity === 1 ? 'item' : 'items'}`;
    if (accessibleCount && accessibleCount.textContent !== accessibleText) {
      accessibleCount.textContent = accessibleText;
    }
    const themeCount = document.querySelector('.cart-count-bubble');
    if (themeCount) {
      const visibleCount = themeCount.querySelector('span[aria-hidden="true"]');
      const hiddenCount = themeCount.querySelector('.visually-hidden');
      if (visibleCount) visibleCount.textContent = visibleText;
      if (hiddenCount) hiddenCount.textContent = accessibleText;
      themeCount.style.display = visibleQuantity > 0 ? '' : 'none';
    }
  }

  function updateUpgradePrompt(cart) {
    const content = document.querySelector('.ocu-cart-content--container');
    const lineItems = content?.querySelector('.ocu-cart-line-items');
    let prompt = content?.querySelector('.vitalsop-cart-upgrade');
    const item = (cart.items || []).find(entry => (
      Number(entry.product_id) === VITALSOP_PRODUCT_ID
      && !GIFT_CART_VARIANTS.has(String(entry.variant_id))
    ));

    let upgrade = null;
    if (item) {
      const isBestValue = String(item.variant_id) === CART_UPGRADES.bestValue.variantId
        && Boolean(item.selling_plan_allocation);
      if (!isBestValue) upgrade = CART_UPGRADES.bestValue;
    }

    if (!content || !lineItems || !upgrade) {
      if (prompt) prompt.remove();
      return;
    }

    if (!prompt) {
      prompt = document.createElement('section');
      prompt.className = 'vitalsop-cart-upgrade';
      prompt.setAttribute('aria-label', 'Upgrade your VitalSop order');
      lineItems.insertAdjacentElement('afterend', prompt);
    }
    if (prompt.dataset.targetVariant === upgrade.variantId) return;

    prompt.dataset.targetVariant = upgrade.variantId;
    prompt.innerHTML = `
      <p class="vitalsop-cart-upgrade__badge"></p>
      <h3 class="vitalsop-cart-upgrade__title"></h3>
      <ul class="vitalsop-cart-upgrade__benefits"></ul>
      <button type="button" class="vitalsop-cart-upgrade__button"></button>
    `;
    prompt.querySelector('.vitalsop-cart-upgrade__badge').textContent = upgrade.badge;
    prompt.querySelector('.vitalsop-cart-upgrade__title').textContent = upgrade.title;
    const benefits = prompt.querySelector('.vitalsop-cart-upgrade__benefits');
    upgrade.benefits.forEach(text => {
      const benefit = document.createElement('li');
      benefit.textContent = text;
      benefits.appendChild(benefit);
    });
    const button = prompt.querySelector('.vitalsop-cart-upgrade__button');
    button.textContent = upgrade.button;
    trackUpgradeEvent('VitalSop Cart Upgrade Viewed', {
      target_variant_id: upgrade.variantId,
      target_selling_plan_id: upgrade.planId,
      target_offer: upgrade.supplyDays === '90' ? '90-day subscription' : '30-day subscription',
      target_line_revenue_cents: upgrade.supplyDays === '90' ? 8995 : 4495
    }, 'viewed');
    button.addEventListener('click', () => replaceOfferBundle({
      dataset: { ...upgrade, cartUpgrade: 'true' }
    }, button));
  }

  function enhanceCart() {
    const cart = currentCart();
    if (!cart || !document.querySelector('.ocu-cart-container')) return;
    updateShipping(cart);
    updateCadence(cart);
    updateTotalsPresentation();
    updateGiftPresentation(cart);
    updateUpgradePrompt(cart);
  }

  function scheduleEnhance() {
    cancelAnimationFrame(enhanceFrame);
    enhanceFrame = requestAnimationFrame(enhanceCart);
  }

  new MutationObserver(scheduleEnhance).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
  document.addEventListener('DOMContentLoaded', () => {
    scheduleEnhance();
    scheduleGiftCleanup();
  });
  window.addEventListener('pageshow', () => {
    scheduleEnhance();
    scheduleGiftCleanup();
  });
})();
